import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, fmtDate, formatApiError, waLink } from "@/lib/api";
import { briefingText } from "@/lib/messages";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";

const FIELDS = [
  ["pickup", "Pickup details", "e.g. Driver Ravi (+91 9XXXXXXXXX) will meet you at Port Blair airport arrivals with a name board at 10:30 AM."],
  ["ferry", "Ferry schedule", "e.g. Day 2: Port Blair → Havelock, 06:30 AM, report at Haddo jetty by 05:45 AM"],
  ["notes", "Other notes (hotel, activities)", "Hotel names, check-in times, activity timings..."],
];

export const BriefingDialog = ({ booking, settings, onClose, onChanged }) => {
  const [f, setF] = useState({ pickup: "", ferry: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (booking) setF({ pickup: booking.briefing_pickup || "", ferry: booking.briefing_ferry || "", notes: booking.briefing_notes || "" });
  }, [booking]);

  const save = async (quiet) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/bookings/${booking.id}/briefing`, f);
      onChanged(data);
      if (!quiet) toast.success("Briefing details saved");
      return data;
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const send = async () => {
    const saved = await save(true);
    if (!saved) return;
    const { data } = await api.post(`/admin/bookings/${booking.id}/briefing-sent`);
    onChanged(data);
    toast.success("Briefing marked as sent");
  };

  if (!booking) return null;
  const text = briefingText(booking, settings, f);
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="briefing-dialog">
        <DialogHeader>
          <DialogTitle>Trip briefing · {booking.booking_no}</DialogTitle>
          <DialogDescription>{booking.customer_name} · travelling {fmtDate(booking.travel_date)}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label>
                <Textarea rows={k === "notes" ? 3 : 4} value={f[k]} placeholder={ph} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`briefing-${k}-input`} /></div>
            ))}
            <p className="text-xs text-slate-500">The packing checklist and payment details come from Company Settings.</p>
          </div>
          <div className="space-y-3 min-w-0">
            <Label>WhatsApp message preview</Label>
            <pre className="whitespace-pre-wrap break-words rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-xs text-slate-700 font-sans max-h-[420px] overflow-y-auto" data-testid="briefing-preview">{text}</pre>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <Button variant="outline" disabled={saving} onClick={() => save(false)} data-testid="briefing-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save details"}
          </Button>
          <a href={waLink(booking.phone, text)} target="_blank" rel="noreferrer" onClick={send} data-testid="briefing-send-button"
            className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors">
            <WhatsAppIcon className="h-4 w-4" /> Save & send on WhatsApp
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};
